import {} from '@app/_proto/Protos/genres';
import { type NovelItem, NovelItem_Status, SearchNovelsRequest_SortType } from '@app/_proto/Protos/novels';
import { HomeCollectionSlots } from '@app/domains/home/analytics/posthog/constants';
import HomeCollection from '@app/domains/home/components/HomeCollection';
import HomeGenres from '@app/domains/home/components/HomeGenres';
import HomeTrendingContent from '@app/domains/home/components/HomeTrendingContent';
import useSearchNovels from '@app/domains/novel/hooks/useSearchNovels';
import clsx from 'clsx';
import { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';

export default function HomeTrendingNovels() {
    const { ref, inView } = useInView({
        triggerOnce: true,
    });

    const {
        genres,
        genreContents = [{ genreNovels: Array<undefined>(5).fill(undefined) }],
        isSuccess,
    } = useGenreNovel({ enabled: inView });

    const [selectedGenrePrev, setSelectedGenre] = useState(undefined);
    const selectedGenre = selectedGenrePrev || genres?.[0];

    return (
        <div ref={ref}>
            <HomeCollection
                data-posthog-params={JSON.stringify({
                    collectionSlot: HomeCollectionSlots.Trending,
                    genre: selectedGenre,
                })}
                isLoading={!isSuccess}
            >
                <div className="mb-28 flex items-center">
                    <h3 className="font-set-b20 text-gray-t1">Trending Novels</h3>
                    <Link
                        className="font-set-m14 ml-auto text-gray-700 dark:text-gray-550"
                        to={`/novels?sortType=${SearchNovelsRequest_SortType[SearchNovelsRequest_SortType.Trending]}`}
                    >
                        View All
                    </Link>
                </div>
                <div className="pb-8">
                    <HomeGenres
                        selectedGenre={selectedGenre}
                        genres={genres}
                        onSelect={genre => setSelectedGenre(genre)}
                        isLoading={!isSuccess}
                    />
                </div>
                {genreContents?.map((genreContent, swiperIdx) => (
                    <div
                        key={`${genreContent.genre}-${swiperIdx}`}
                        className={clsx('', {
                            'invisible h-0 !py-0 md:!py-0': genreContent.genre !== selectedGenre,
                        })}
                    >
                        {genreContent.genreNovels?.map((content: NovelItem | undefined, idx) => (
                            <div
                                key={`${genreContent.genre}-novel-${content?.id}-${idx}`}
                                className="border-b border-solid border-gray-100 dark:border-gray-800 last:border-none"
                                data-posthog-params={JSON.stringify({ itemSlot: idx })}
                            >
                                <HomeTrendingContent content={content} rank={idx + 1} isLoading={!isSuccess} />
                            </div>
                        ))}
                    </div>
                ))}
            </HomeCollection>
        </div>
    );
}

type UseGenreNovelParam = {
    enabled?: boolean;
};
type UseGenreNovelReturn = {
    genres: string[] | undefined;
    genreContents: { genre: string; genreNovels: NovelItem[] | undefined }[] | undefined;
    isSuccess: boolean;
};
const useGenreNovel = ({ enabled }: UseGenreNovelParam): UseGenreNovelReturn => {
    /*const { data: genres, isSuccess: isGenreSuccess } = useGenres({
        level: GenreLevel.MainGenreLevel,
        contentType: [GenreContent.NovelGenreContent],
        enabled,
    });*/

    const genres = ['Cultivation', 'Fantasy', 'Comedy', 'Mystery', 'Romance', 'Sci-fi'];

    const { data: novels, isSuccess } = useSearchNovels({
        genres: genres || ALL_GENRES_PARAM,
        sortType: SearchNovelsRequest_SortType.Trending,
        status: NovelItem_Status.All,
        enabled: enabled, //&& isGenreSuccess,
        count: AVERAGE_ITEM_COUNT * (genres?.length ?? MIN_GENRE_COUNT),
        championFilter: {
            excludeCompletedNovelsWithoutChampion: true,
        },
    });

    const genreContents = useMemo(() => {
        if (!novels) return undefined;

        const groupedContents = genres?.map(genre => {
            const genreNovels = novels
                .slice()
                .filter(d => {
                    return d.genres.includes(genre);
                })
                .slice(0, MIN_GROUP_NOVEL_COUNT);

            const genreRank = genreNovels?.at(0)?.trendingScore ?? (genres?.length ?? 0) + 1;
            return { genre, genreNovels, genreRank };
        });
        return groupedContents?.sort((a, b) => {
            return a.genreRank - b.genreRank;
        });
    }, [novels]);

    const availableGenres = genreContents
        ?.filter(contents => (contents?.genreNovels?.length ?? 0) > 0)
        ?.map(contents => contents.genre);

    //return { genres: availableGenres, genreContents, isSuccess: isGenreSuccess && isSuccess };
    return { genres: availableGenres, genreContents, isSuccess: isSuccess };
};

const AVERAGE_ITEM_COUNT = 15;
const ALL_GENRES_PARAM = [];
const MIN_GENRE_COUNT = 5;
const MIN_GROUP_NOVEL_COUNT = 5;
